import React from 'react'

import { Heading } from '@tiptap/extension-heading'
import Highlight from '@tiptap/extension-highlight'
import { HorizontalRule } from '@tiptap/extension-horizontal-rule'
import { Link } from '@tiptap/extension-link'
import {
  Table,
  TableCell,
  TableHeader,
  TableRow,
} from '@tiptap/extension-table'
import { BackgroundColor, Color, TextStyle } from '@tiptap/extension-text-style'
import { Underline } from '@tiptap/extension-underline'
import type { JSONContent } from '@tiptap/react'
import { generateJSON } from '@tiptap/react'
import { StarterKit } from '@tiptap/starter-kit'
import { defaultSchema } from 'hast-util-sanitize'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize from 'rehype-sanitize'
import rehypeStringify from 'rehype-stringify'
import directive from 'remark-directive'
import remarkGfm from 'remark-gfm'
import remarkParse from 'remark-parse'
import remark2rehype from 'remark-rehype'
import { unified } from 'unified'
import { visit } from 'unist-util-visit'

import { remarkHighlightMarks } from '@/components/common/MarkdownViewer/remarkHighlightMarks'
import { htmlDirectives } from '@/components/common/TextArea/MarkdownTextArea/lib'
import { ShiftEnterMessage } from '@/components/common/TextArea/RichTextArea/ShiftEnterMessage'

import { ChatCitation } from '@/extensions/ChatCitation'
import { CHAT_CITATION_NODE_NAME } from '@/extensions/ChatCitation/lib'

const remarkMentions = () => (tree: any) => {
  visit(tree, 'textDirective', (node, index, parent) => {
    if (node.name === 'mention') {
      const label = node.children[0]?.value
      const id = node.attributes?.['id']
      const type = node.attributes?.['type']

      if (index !== undefined && parent !== undefined) {
        parent.children[index] = {
          type: 'html',
          value: `<span data-type="mention" data-id="${id}" data-label="${label}" data-mention-type="${type}">@${label}</span>`,
        }
      } else {
        node.type = 'html'
        node.value = `<span data-type="mention" data-id="${id}" data-label="${label}" data-mention-type="${type}">@${label}</span>`
      }
    }
  })
}

const processor = unified()
  .use(remarkParse)
  .use(remarkGfm) // This handles GFM tables and other GitHub-flavored markdown features
  .use(remarkHighlightMarks)
  .use(directive)
  .use(htmlDirectives)
  .use(remarkMentions)
  .use(remark2rehype, { allowDangerousHtml: true })
  .use(rehypeRaw)
  .use(rehypeSanitize, {
    ...defaultSchema,
    tagNames: [
      ...(defaultSchema.tagNames || []),
      'span',
      'font',
      'mark',
      'u',
      'sup',
      'sub',
      'blockquote',
      'chat-citation',
    ],
    attributes: {
      ...defaultSchema.attributes,
      'chat-citation': ['data*', 'dataSources', 'dataType'],
      span: [
        // DO NOT REMOVE OR ELSE MENTIONS WON'T WORK
        'data*',
        'user-id',
        'className',
        'class',
        'style',
      ],
      font: ['color', 'face', 'size', 'style'],
      mark: ['style'],
    },
  })
  .use(rehypeStringify)

const processChildren = (content: JSONContent): string => {
  let markdown = ''
  content.content?.forEach((child) => {
    markdown += jsonToMarkdown(child)
  })
  return markdown
}

const getListItemMarkdown = (content: JSONContent): string => {
  return processChildren(content)
    .trim()
    .replace(/\n{2,}/g, '\n')
}

const isFencedCodeDelimiterStart = (
  markdown: string,
  index: number,
): boolean => {
  if (!markdown.startsWith('```', index)) {
    return false
  }

  const lineStart = markdown.lastIndexOf('\n', index - 1) + 1
  return /^[\t ]*$/.test(markdown.slice(lineStart, index))
}

/*
This is the Typescript equivalent of the jsonToMarkdown function in the backend
at sweetspot/server_v2/server_v2/v1/services/hooks/tiptap.py.

NOTE: this uses GitHub Flavored Markdown (GFM) syntax.

NOTE: any changes made here should be mirrored in the Python code.

NOTE: this does not handle all tiptap node types, just the ones we use in the RichTextArea.
So, please only use this for simple cases.
*/
export const jsonToMarkdown = (content: JSONContent): string => {
  if ('default' in content) {
    // typically, tiptap documents contain all of their content in 'default' fragment
    // of the yjs map; so, we need to recursively call this function on the default
    // fragment if it exists
    return jsonToMarkdown(content.default)
  }
  if (content.type === 'text') {
    let text = content.text || ''
    content.marks?.forEach((mark) => {
      switch (mark.type) {
        case 'bold':
          text = `__${text}__`
          break
        case 'italic':
          text = `*${text}*`
          break
        case 'strike':
          text = `~~${text}~~`
          break
        case 'highlight':
          text = `==${text}==`
          break
        case 'underline':
          text = `<u>${text}</u>`
          break
        case 'superscript':
          text = `<sup>${text}</sup>`
          break
        case 'subscript':
          text = `<sub>${text}</sub>`
          break
        case 'code':
          text = `\`${text}\``
          break
        case 'link':
          const href = mark.attrs?.href || '#'
          text = `[${text}](${href})`
          break
      }
    })
    return text
  }

  // handle nested nodes first
  let markdown = processChildren(content).trim()

  switch (content.type) {
    case 'table':
      markdown = ''
      content.content?.forEach((row, idx) => {
        const rowMarkdown = jsonToMarkdown(row)
        markdown += rowMarkdown + '\n'
        if (idx === 0) {
          const numColumns = rowMarkdown.split('|').length - 2
          const headerSeparator =
            '|' + Array(numColumns).fill('---').join('|') + '|'
          markdown += headerSeparator + '\n'
        }
      })
      return markdown + '\n\n'

    case 'tableRow':
      let rowContent = '|'
      content.content?.forEach((cell) => {
        rowContent += processChildren(cell).trim() + '|'
      })
      return rowContent

    case 'tableCell':
      return processChildren(content).trim()

    case 'tableHeader':
      return processChildren(content).trim()

    case 'heading':
      const level = content.attrs?.level || 1
      markdown = `${'#'.repeat(level)} `
      markdown += processChildren(content)
      markdown += '\n\n'
      return markdown

    case 'paragraph':
      return markdown.trim() + '\n\n'

    case 'bulletList':
      const bulletListItems =
        content.content
          ?.map((item) => {
            const listItemContent = getListItemMarkdown(item)
            return listItemContent ? `- ${listItemContent}` : ''
          })
          .filter(Boolean)
          .join('\n') ?? ''
      return bulletListItems ? `${bulletListItems}\n\n` : ''

    case 'orderedList':
      const orderedListStart =
        typeof content.attrs?.start === 'number' ? content.attrs.start : 1
      const orderedListItems =
        content.content
          ?.map((item, index) => {
            const listItemContent = getListItemMarkdown(item)
            return listItemContent
              ? `${orderedListStart + index}. ${listItemContent}`
              : ''
          })
          .filter(Boolean)
          .join('\n') ?? ''
      return orderedListItems ? `${orderedListItems}\n\n` : ''

    case 'listItem':
      return markdown.trim()

    case 'blockquote':
      // Each line in a blockquote needs to start with '> '
      const blockquoteContent = processChildren(content).trim()
      markdown = blockquoteContent
        .split('\n')
        .map((line) => `> ${line}`)
        .join('\n')
      markdown += '\n\n'
      return markdown

    case 'codeBlock':
      const language = content.attrs?.language || ''
      markdown = `\`\`\`${language}\n`
      markdown += processChildren(content)
      markdown += '\n```\n'
      return markdown

    case 'horizontalRule':
      return '---\n'

    case 'image':
      const alt = content.attrs?.alt || ''
      const src = content.attrs?.src || ''
      return `![${alt}](${src})\n`

    case 'mention':
      const label = content.attrs?.label || ''
      const id = content.attrs?.id || ''
      const type = content.attrs?.type || 'user'
      return `:mention[${label}]{type=${type} id=${id}}`

    case CHAT_CITATION_NODE_NAME:
      const sources = Array.isArray(content.attrs?.sources)
        ? content.attrs.sources
        : []
      return sources
        .map((source) => (source?.id != null ? `[${String(source.id)}]` : ''))
        .join('')

    default:
      return markdown
  }
}

const splitMarkdownChunks = (markdown: string): string[] => {
  const chunks: string[] = []
  let currentChunk = ''
  let inFencedCodeBlock = false

  let i = 0
  while (i < markdown.length) {
    if (isFencedCodeDelimiterStart(markdown, i)) {
      inFencedCodeBlock = !inFencedCodeBlock
    }

    const char = markdown[i]

    if (
      !inFencedCodeBlock &&
      char.charCodeAt(0) === 10 &&
      i > 0 &&
      markdown[i - 1] !== '|'
    ) {
      // We found a "real" paragraph break
      if (currentChunk.trim()) {
        chunks.push(currentChunk.trim())
      } else if (
        // check for triple new lines since the jsonToMarkdown function above
        // will convert double new lines to a blank paragraph;
        // an example triple new line looks like this: blahblahblah\n\n\n, where the first newline
        // is part of the previous chunk and the last two newlines are the "marker" for a blank paragraph;
        // TODO: this is a hack and we should eventually come up with a better way to handle this
        markdown[i - 1].charCodeAt(0) === 10 &&
        i < markdown.length - 1 &&
        markdown[i + 1].charCodeAt(0) === 10
      ) {
        // we found a newline, so we should add a blank paragraph
        chunks.push('')
        i += 1
      }
      currentChunk = ''
    } else {
      currentChunk += char
    }
    i++
  }

  // don't forget the last chunk
  if (currentChunk.trim()) {
    chunks.push(currentChunk.trim())
  }

  return chunks
}

export const markdownToHtml = (markdown: string): string => {
  const chunks = splitMarkdownChunks(markdown)

  // process each new line chunk;
  // we need to do this because otherwise blank lines will be ignored
  const processedChunks = chunks.map((chunk) => {
    if (chunk.length === 0) {
      return `<p></p>` // empty chunks become paragraph breaks
    }
    return String(processor.processSync(chunk))
  })

  const html = processedChunks.join('\n')
  return html
    .replace(/<font\b([^>]*)>/gi, (_match, attributes: string) => {
      const colorMatch = attributes.match(
        /\bcolor\s*=\s*(?:"([^"]+)"|'([^']+)'|([^\s>]+))/i,
      )
      const color = colorMatch?.[1] ?? colorMatch?.[2] ?? colorMatch?.[3]

      return color != null ? `<span style="color: ${color};">` : '<span>'
    })
    .replace(/<\/font>/gi, '</span>')
}

export const isShiftEnterMessage = (
  element: React.ReactNode,
): element is React.ReactElement => {
  return (
    React.isValidElement(element) &&
    (element.type === ShiftEnterMessage ||
      (typeof element.type === 'function' &&
        element.type.name === 'ShiftEnterMessage'))
  )
}

export const generateUserMentionText = (label: string, id: string) => {
  return `:mention[${label}]{type=user id=${id}}`
}

export const parseJsonContent = (content: string) => {
  if (!content) {
    return null
  }
  const firstChar = content.trimStart().charCodeAt(0)
  // JSON.parse only accepts values starting with `{`, `[`, `"`, a digit, `-`,
  // `t`(rue), `f`(alse), or `n`(ull). Anything else — e.g. legacy markdown
  // stored by older playbooks — is plain text and should short-circuit.
  if (firstChar !== 0x7b /* { */ && firstChar !== 0x5b /* [ */) {
    return null
  }
  try {
    const parsed = JSON.parse(content)
    if (parsed && typeof parsed === 'object' && 'default' in parsed) {
      return parsed.default
    }
    return parsed
  } catch {
    return null
  }
}

export const getPlainTextFromJsonContent = (
  content: string | JSONContent,
): string => {
  if (typeof content === 'string') {
    return getPlainTextFromJsonContent(parseJsonContent(content))
  }
  if (!content) {
    return ''
  }

  let text = ''

  // If this node has text content, add it
  if (content.text) {
    text += content.text
  }

  // Recursively process child nodes
  if (content.content && Array.isArray(content.content)) {
    for (const child of content.content) {
      text += getPlainTextFromJsonContent(child)
    }
  }

  return text
}

// Extensions for markdown to JSON conversion
const markdownToJsonExtensions = [
  StarterKit.configure({
    heading: false,
    horizontalRule: false,
    link: false,
    underline: false,
  }),
  ChatCitation,
  Heading.configure({
    levels: [1, 2, 3, 4, 5, 6],
  }),
  Highlight.configure({
    multicolor: true,
  }),
  HorizontalRule,
  Underline,
  Link.configure({
    openOnClick: false,
  }),
  TextStyle,
  Color,
  BackgroundColor,
  Table,
  TableRow,
  TableCell,
  TableHeader,
]

export const stripEmptyTextNodes = (
  content: JSONContent,
): JSONContent | null => {
  if (content.type === 'text' && (content.text ?? '').length === 0) {
    return null
  }

  const nextContent =
    content.content
      ?.map((child) => stripEmptyTextNodes(child))
      .filter((child): child is JSONContent => child != null) ?? undefined

  return {
    ...content,
    ...(nextContent ? { content: nextContent } : {}),
  }
}

/**
 * Convert markdown text to tiptap JSON content.
 * First converts markdown to HTML, then parses HTML to tiptap JSON.
 */
export const markdownToJson = (markdown: string): JSONContent => {
  if (!markdown || markdown.trim() === '') {
    return { type: 'doc', content: [] }
  }
  const html = markdownToHtml(markdown)
  const json = generateJSON(html, markdownToJsonExtensions)
  return stripEmptyTextNodes(json) ?? { type: 'doc', content: [] }
}
